import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDate } from 'src/utils/formatTime';
import { getUnmatchedFileStatusLabel } from 'src/utils/enums/unmatchedFileStatus';
import { getUnmatchedSignerTrustSignal } from 'src/utils/enums/unmatchedSignerTrustSignal';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import {
  useUnmatchedFileSummary,
  useUnmatchedFileSigners,
  useUnmatchedFileMatchedApps,
  useInfiniteUnmatchedFileEvents,
  useUpdateUnmatchedFileStatuses,
} from 'src/hooks/wdac';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import StatusButton from 'src/components/StatusButton';
import TruncatedText from 'src/components/TruncatedText';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableCard from 'src/components/Table/TableCard';
import { DetailCard } from 'src/components/summary/DetailCard';
import { SummaryCardSkeleton } from 'src/components/summary/SummaryCardSkeleton';
import { SummaryGrid } from 'src/components/summary/SummaryGrid';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { HashText } from './UnmatchedGroupTableShared';
import UnmatchedFileStatusModal from './UnmatchedFileStatusModal';
import CatalogTrustWarning from './CatalogTrustWarning';
import { UNMATCHED_FILE_GROUP_BY } from './constants';

const compactCellSx = {
  fontSize: '0.7rem',
  py: 0.75,
  lineHeight: 1.3,
};

const compactHeaderSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  py: 0.75,
};

const clickableRowSx = {
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#565656' },
};

const SignerTrustSignalChip = ({ signal }) => {
  const mapping = getUnmatchedSignerTrustSignal(signal);
  const label = mapping?.label ?? 'None';
  const color = mapping?.color ?? '#9CA3AF';
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        borderColor: color,
        color,
        backgroundColor: 'transparent',
        height: 20,
        fontSize: '0.65rem',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
};

const MatchedAppChip = () => (
  <Chip
    label="Matched App"
    size="small"
    variant="outlined"
    sx={{
      borderColor: '#7FB1D6',
      color: '#7FB1D6',
      backgroundColor: 'transparent',
      height: 20,
      fontSize: '0.65rem',
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
);

const UnmatchedFileDetailsModal = ({
  open,
  onClose,
  statusId,
  fallbackRow,
  onViewFilesForGroup,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [statusSnapshot, setStatusSnapshot] = useState(null);
  const updateStatuses = useUpdateUnmatchedFileStatuses();
  const summaryQuery = useUnmatchedFileSummary({ statusId, enabled: open });
  const summary = summaryQuery.data;

  const fileId = summary?.fileId ?? fallbackRow?.fileId;

  const signersQuery = useUnmatchedFileSigners({ fileId, enabled: open });
  const matchedAppsQuery = useUnmatchedFileMatchedApps({ fileId, enabled: open });

  const eventsQuery = useInfiniteUnmatchedFileEvents({ fileId, enabled: open });

  const events = useMemo(
    () => eventsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [eventsQuery.data],
  );

  const loadMoreEvents = useCallback(() => {
    if (eventsQuery.hasNextPage) {
      eventsQuery.fetchNextPage();
    }
  }, [eventsQuery]);

  const [eventsSentryRef] = useInfiniteScroll({
    loading: eventsQuery.isFetchingNextPage,
    hasNextPage: eventsQuery.hasNextPage,
    disabled: !open,
    onLoadMore: loadMoreEvents,
    rootMargin: '0px 0px 240px 0px',
  });

  const goToDevice = useCallback(
    deviceId => {
      if (!deviceId) return;
      sessionStorage.setItem(
        DEVICES_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      onClose?.();
      navigate(`${PORTAL_ROUTE}/devices/${deviceId}/unmatched-files`);
    },
    [navigate, onClose],
  );

  const goToSignerFiles = useCallback(
    signer => {
      if (!signer?.tbsHash || !onViewFilesForGroup) return;
      onClose?.();
      onViewFilesForGroup({
        kind: 'signer',
        groupLabel: 'Publisher TBS',
        groupValue: signer.tbsHash,
        label: `Publisher TBS: ${signer.tbsHash}`,
        params: { groupBy: UNMATCHED_FILE_GROUP_BY.TbsHash, groupKey: signer.tbsHash },
      });
    },
    [onViewFilesForGroup, onClose],
  );

  const goToMatchedApp = useCallback(
    app => {
      if (!app?.customerEnvAppId) return;
      onClose?.();
      navigate(`${PORTAL_ROUTE}/apps/${app.customerEnvAppId}/general`, {
        state: { backTo: `${PORTAL_ROUTE}/wdac-results/unmatched-files` },
      });
    },
    [navigate, onClose],
  );

  const title =
    summary?.fileName || fallbackRow?.fileName || fallbackRow?.sha256 || 'Unmatched File Details';

  const trustStatusValue = summary?.trustStatus ?? fallbackRow?.trustStatus;
  const trustStatusLabel = getUnmatchedFileStatusLabel(trustStatusValue);
  const note = summary?.note ?? fallbackRow?.note;

  const fileSigners = signersQuery.data || [];
  const matchedApps = useMemo(
    () => (matchedAppsQuery.data || []).filter(app => app.name),
    [matchedAppsQuery.data],
  );

  // Each list resolves on its own schedule, so until one arrives the counts the summary already
  // carries decide whether to render a loading skeleton (data still incoming) or nothing at all
  // (this file genuinely has none).
  const signersReady = !!signersQuery.data;
  const matchedAppsReady = !!matchedAppsQuery.data;
  const showSignersCard = signersReady ? fileSigners.length > 0 : (summary?.numSigners ?? 0) > 0;
  const showMatchedAppsCard = matchedAppsReady
    ? matchedApps.length > 0
    : (summary?.numTrustedGlobalApps ?? 0) > 0;

  const statusRow = useMemo(
    () => ({
      id: statusId,
      trustStatus: trustStatusValue,
      note: summary?.note ?? fallbackRow?.note,
      fileName: title,
      sha256: summary?.sha256 ?? fallbackRow?.sha256,
    }),
    [statusId, trustStatusValue, summary, fallbackRow, title],
  );

  const openStatusModal = useCallback(() => {
    setStatusSnapshot(statusRow);
    onClose?.();
  }, [statusRow, onClose]);

  const handleSaveStatus = useCallback(
    async (newStatus, note) => {
      if (!statusSnapshot?.id) return;
      try {
        await updateStatuses.mutateAsync({
          customerEnvironmentUnmatchedFileStatusIds: [statusSnapshot.id],
          status: newStatus,
          notes: note,
        });
        enqueueSnackbar('Status updated', { variant: 'success', autoHideDuration: 3000 });
        setStatusSnapshot(null);
      } catch (error) {
        enqueueSnackbar(`Could not update status: ${error?.message || error}`, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [statusSnapshot, updateStatuses, enqueueSnackbar],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
        <DialogTitle sx={{ pr: 5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <span>{title}</span>
            <StatusButton
              status={trustStatusValue}
              onClick={openStatusModal}
              disabled={!statusId}
              sx={{ height: 26, fontSize: '0.72rem', py: 0.25, px: 1.25 }}>
              {trustStatusLabel}
            </StatusButton>
            <CatalogTrustWarning
              trustStatus={trustStatusValue}
              trustedAsOfVersion={summary?.trustedAsOfVersion}
              width={20}
              height={20}
            />
          </Stack>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
            <Iconify icon="mdi:close" width={20} height={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {summaryQuery.isLoading ? (
              <SummaryCardSkeleton rows={3} />
            ) : (
              <DetailCard compact>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2">File Summary</Typography>
                  <SummaryValue compact label="SHA-256 Hash">
                    {summary?.sha256 || fallbackRow?.sha256}
                  </SummaryValue>
                  <SummaryValue compact label="Authenticode SHA-256 Hash">
                    {summary?.authenticodeHashSha256}
                  </SummaryValue>
                  <SummaryGrid compact>
                    <SummaryValue compact label="Original File Name">
                      {summary?.originalFileName}
                    </SummaryValue>
                    <SummaryValue compact label="Product Name">
                      {summary?.productName}
                    </SummaryValue>
                    <SummaryValue compact truncate label="File Version">
                      {summary?.fileVersion}
                    </SummaryValue>
                  </SummaryGrid>
                  <SummaryGrid compact>
                    <SummaryValue compact label="Last Seen">
                      {summary?.lastSeenDate
                        ? formatDate(summary.lastSeenDate, 'yyyy-MM-dd')
                        : '--'}
                    </SummaryValue>
                    <SummaryValue compact label="Last 30 Event Count">
                      {summary?.last30EventCount ?? 0}
                    </SummaryValue>
                    <SummaryValue compact label="Last 90 Event Count">
                      {summary?.last90EventCount ?? 0}
                    </SummaryValue>
                  </SummaryGrid>
                  <SummaryGrid compact>
                    <SummaryValue
                      compact
                      label="Status Updated"
                      valueAdornment={
                        note || summary?.trustedAsOfVersion ? (
                          <Tooltip
                            arrow
                            componentsProps={{
                              tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 480 } },
                            }}
                            title={
                              <Stack gap={0.75} sx={{ py: 0.25 }}>
                                {summary?.trustedAsOfVersion ? (
                                  <Chip
                                    size="small"
                                    label={`Trusted as of ${summary.trustedAsOfVersion}`}
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      alignSelf: 'flex-start',
                                      color: '#E5E7EB',
                                      backgroundColor: 'rgba(255, 255, 255, 0.10)',
                                      border: '1px solid rgba(255, 255, 255, 0.25)',
                                    }}
                                  />
                                ) : null}
                                {note ? (
                                  <Typography sx={{ color: '#F3F4F6', fontSize: '0.72rem' }}>
                                    {note}
                                  </Typography>
                                ) : null}
                              </Stack>
                            }>
                            <Box display="inline-flex" alignItems="center" justifyContent="center">
                              <Image
                                src="/static/icons/notes-notepad.svg"
                                alt="Status details"
                                width={14}
                                height={14}
                              />
                            </Box>
                          </Tooltip>
                        ) : null
                      }>
                      {summary?.statusUpdatedOn
                        ? formatDate(summary.statusUpdatedOn, 'yyyy-MM-dd')
                        : '--'}
                    </SummaryValue>
                    <SummaryValue compact label="Last 30 Device Count">
                      {summary?.numDevicesWithEventsWithin30OfLastSeen ?? 0}
                    </SummaryValue>
                    <SummaryValue compact label="Last 90 Device Count">
                      {summary?.numDevicesWithEventsWithin90OfLastSeen ?? 0}
                    </SummaryValue>
                  </SummaryGrid>
                </Stack>
              </DetailCard>
            )}

            {showSignersCard && (
              <TableCard title="File Signers" maxHeight={190} edgePadding={3}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                    <TableRow>
                      <TableCell sx={{ ...compactHeaderSx, width: '22%' }}>
                        Publisher Name
                      </TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '19%' }}>
                        Publisher TBS Hash
                      </TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '22%' }}>Issuer Name</TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '19%' }}>
                        Issuer TBS Hash
                      </TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '18%' }}>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                    {!signersReady && <LoadingRows columnCount={5} rowCount={3} />}
                    {signersReady &&
                      fileSigners.map((signer, index) => {
                        const clickable = !!signer.tbsHash && !!onViewFilesForGroup;
                        return (
                          <TableRow
                            key={`${signer.tbsHash}-${index}`}
                            onClick={clickable ? () => goToSignerFiles(signer) : undefined}
                            sx={clickable ? clickableRowSx : undefined}>
                            <TableCell sx={compactCellSx}>
                              <TruncatedText value={(signer.publisherName || '').trim()} bold />
                            </TableCell>
                            <TableCell sx={{ ...compactCellSx, maxWidth: 0 }}>
                              <HashText value={signer.tbsHash} />
                            </TableCell>
                            <TableCell sx={compactCellSx}>
                              <TruncatedText value={(signer.issuerName || '').trim()} muted />
                            </TableCell>
                            <TableCell sx={{ ...compactCellSx, maxWidth: 0 }}>
                              <HashText value={signer.issuerTbsHash} />
                            </TableCell>
                            <TableCell sx={compactCellSx}>
                              <SignerTrustSignalChip signal={signer.trustSignal} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableCard>
            )}

            {showMatchedAppsCard && (
              <TableCard title="Matched Apps" maxHeight={190} edgePadding={3}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                    <TableRow>
                      <TableCell sx={{ ...compactHeaderSx, width: '38%' }}>Application</TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '28%' }}>Publisher</TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '18%' }}>Source</TableCell>
                      <TableCell sx={{ ...compactHeaderSx, width: '16%' }}>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                    {!matchedAppsReady && <LoadingRows columnCount={4} rowCount={3} />}
                    {matchedAppsReady &&
                      matchedApps.map((app, index) => {
                        const clickable = !!app.customerEnvAppId;
                        return (
                          <TableRow
                            key={`matched-app-${index}`}
                            onClick={clickable ? () => goToMatchedApp(app) : undefined}
                            sx={clickable ? clickableRowSx : undefined}>
                            <TableCell sx={compactCellSx}>
                              <TruncatedText
                                value={[app.name?.trim(), app.version?.trim()]
                                  .filter(Boolean)
                                  .join(' ')}
                                bold
                              />
                            </TableCell>
                            <TableCell sx={compactCellSx}>
                              <TruncatedText value={(app.publisher || '').trim()} muted />
                            </TableCell>
                            <TableCell sx={compactCellSx}>
                              <TruncatedText value={app.source} muted />
                            </TableCell>
                            <TableCell sx={compactCellSx}>
                              <MatchedAppChip />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableCard>
            )}

            <TableCard title="Recent Devices" maxHeight={260} edgePadding={3}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                  <TableRow>
                    <TableCell sx={{ ...compactHeaderSx, width: '18%' }}>Device Name</TableCell>
                    <TableCell sx={{ ...compactHeaderSx, width: '12%' }}>Date</TableCell>
                    <TableCell sx={{ ...compactHeaderSx, width: '22%' }}>File Name</TableCell>
                    <TableCell sx={{ ...compactHeaderSx, width: '24%' }}>
                      Launching Process
                    </TableCell>
                    <TableCell sx={{ ...compactHeaderSx, width: 'auto' }}>File Path</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                  {eventsQuery.isLoading ? (
                    <LoadingRows columnCount={5} rowCount={4} />
                  ) : (
                    <>
                      {events.map((event, index) => (
                        <TableRow
                          key={`${event.deviceId}-${event.eventSystemTime}-${index}`}
                          ref={index === events.length - 1 ? eventsSentryRef : null}
                          onClick={() => goToDevice(event.deviceId)}
                          sx={clickableRowSx}>
                          <TableCell sx={compactCellSx}>
                            <TruncatedText value={event.deviceName} bold />
                          </TableCell>
                          <TableCell sx={compactCellSx}>
                            <TruncatedText
                              value={
                                event.eventSystemTime
                                  ? formatDate(event.eventSystemTime, 'yyyy-MM-dd')
                                  : ''
                              }
                              muted
                            />
                          </TableCell>
                          <TableCell sx={compactCellSx}>
                            <TruncatedText value={event.fileName} muted />
                          </TableCell>
                          <TableCell sx={compactCellSx}>
                            <TruncatedText
                              value={event.processName}
                              muted
                              fontSize="0.6rem"
                              tooltip
                            />
                          </TableCell>
                          <TableCell sx={compactCellSx}>
                            <TruncatedText
                              value={event.fullFilePath}
                              muted
                              fontSize="0.6rem"
                              tooltip
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {!eventsQuery.isLoading && eventsQuery.isFetchingNextPage && (
                        <LoadingRows columnCount={5} rowCount={2} />
                      )}
                      {!eventsQuery.isFetching && !events.length && (
                        <NoDataCell colSpan={5}>No events found.</NoDataCell>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableCard>
          </Stack>
        </DialogContent>
      </Dialog>

      <UnmatchedFileStatusModal
        open={!!statusSnapshot}
        onClose={() => setStatusSnapshot(null)}
        rows={statusSnapshot ? [statusSnapshot] : []}
        onSave={handleSaveStatus}
        loading={updateStatuses.isLoading}
      />
    </>
  );
};

export default UnmatchedFileDetailsModal;
